import { useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Circle, Play, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { devServersMeta, actionRunningLabel, devProgressLine } from './live-ui';
import { runDevServerAction, type DevServerActionKind } from './dev-server-actions';
import type { TaskDevServers } from '@shared/types/factory';
import { DEV_IDLE_MINUTES_MAX, DEV_IDLE_MINUTES_MIN, devIdleText, type DevProgress, type DevStepState } from '@shared/dev-servers';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { ExternalLink } from '@/components/ExternalLink';
import { NumberInput } from '@/components/NumberInput';
import { Spinner } from '@/components/ui/spinner';
import { describeApiError } from '@/lib/api-errors';
import { formatRelative } from '@/lib/formatters';
import { setTaskDevIdle } from '@/lib/dev-servers-api';
import { useDevProgress } from './use-task-dev-servers';
import { cn } from '@/lib/utils';

/** SF-T-209: a task's worktree and its dev servers: their state, links, and Start, Stop and Tidy. */

/** Its servers' links: the Tailscale one when published, else local; a stopped server's port, unlinked. */
export function DevServerLinks({ servers, className }: { servers: TaskDevServers['servers']; className?: string }) {
  if (!servers.length) return null;
  return (
    <ul className={className ?? 'space-y-0.5 text-xs'} aria-label="Dev servers">
      {servers.map((s) => (
        <li key={s.port} className="flex flex-wrap items-baseline gap-x-2">
          <span className="w-16 shrink-0 text-muted-foreground">{s.role ?? 'server'}</span>
          {s.listening ? (
            <>
              <ExternalLink href={s.url ?? s.local_url} size="xs">
                {(s.url ?? s.local_url).replace(/^https?:\/\//, '')}
              </ExternalLink>
              {s.url && s.url !== s.local_url ? (
                <ExternalLink href={s.local_url} size="xs" variant="muted">
                  local
                </ExternalLink>
              ) : null}
            </>
          ) : (
            <span className="text-muted-foreground tabular-nums">:{s.port} not listening</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function StepIcon({ state }: { state: DevStepState }) {
  if (state === 'current') return <Spinner size="xs" />;
  if (state === 'done') return <Check className="size-3" aria-hidden />;
  if (state === 'failed') return <AlertTriangle className="size-3" aria-hidden />;
  return <Circle className="size-2.5" aria-hidden />;
}

const STEP_TEXT: Record<DevStepState, string> = {
  done: 'text-success-strong',
  current: 'font-medium text-foreground',
  todo: 'text-muted-foreground',
  failed: 'text-warning-strong',
};

const STEPS_LABEL: Record<DevProgress['action'], string> = {
  start: 'How far the start has got',
  stop: 'How far the stop has got',
  tidy: 'How far the tidy has got',
};

/**
 * SF-T-231: how far a Start, Stop or Tidy has got, as a row of steps: sent to the machine, its steps there (a
 * Start: the worktree made again and set up, the servers started; a Tidy: servers, database branch, worktree),
 * and the machine's report showing it. `compact` (the Board's dialog): only the step it's on, with a line.
 */
export function DevProgressSteps({ progress, message, compact = false }: { progress: DevProgress; message?: string | null; compact?: boolean }) {
  const at = Math.max(
    0,
    progress.steps.findIndex((s) => s.state === 'current' || s.state === 'failed')
  );
  if (compact) {
    const step = progress.steps[at];
    return (
      <p className={cn('flex min-w-0 items-center gap-1.5 text-xs', step.state === 'failed' ? 'text-warning-strong' : 'text-muted-foreground')}>
        <StepIcon state={step.state} />
        <span className="shrink-0">
          Step {at + 1} of {progress.steps.length}: {step.label}
        </span>
        {message ? <span className="min-w-0 truncate">· {message}</span> : null}
      </p>
    );
  }
  return (
    <ol aria-label={STEPS_LABEL[progress.action]} className="flex flex-wrap items-center gap-x-1 gap-y-1 text-xs">
      {progress.steps.map((s, n) => (
        <li key={s.key} className={cn('flex items-center gap-1', STEP_TEXT[s.state])} aria-current={s.state === 'current' ? 'step' : undefined}>
          {n > 0 ? <ChevronRight className="size-3 text-muted-foreground" aria-hidden /> : null}
          <StepIcon state={s.state} />
          {s.label}
          {s.state === 'done' || s.state === 'failed' ? <span className="sr-only">({s.state === 'done' ? 'done' : "hasn't happened"})</span> : null}
        </li>
      ))}
    </ol>
  );
}

/** Start, Stop and Tidy as the person may (Start: Members; Stop and Tidy: Maintainers) and the worktree allows. */
export function DevServerActions({
  devServers: d,
  canStart,
  canStop,
  onDone,
  project,
  run = runDevServerAction,
}: {
  devServers: TaskDevServers;
  canStart: boolean;
  canStop: boolean;
  onDone?: () => void;
  project?: string;
  /** Preview only (the /dev/ui gallery). */
  run?: typeof runDevServerAction;
}) {
  const [busy, setBusy] = useState<DevServerActionKind | null>(null);
  // SF-T-231: an action stays busy until a report shows it (servers listening, stopped, the worktree gone), not
  // only while the machine works on it.
  const progress = useDevProgress(d);
  const pending = progress?.phase === 'working' || progress?.phase === 'awaiting' ? progress.action : null;
  const working = d.action?.status === 'running' || Boolean(pending);
  const runActive = ['queued', 'running', 'waiting'].includes(d.run.status);
  const go = async (action: DevServerActionKind) => {
    setBusy(action);
    await run(d.task.id, action, project);
    setBusy(null);
    onDone?.();
  };
  const offline = !d.machine.online;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {canStart && !d.running ? (
        <Button
          variant="outline"
          size="xs"
          disabled={offline || working}
          loading={busy === 'start' || pending === 'start'}
          loadingText="Starting…"
          onClick={() => void go('start')}
          data-hint={d.exists ? 'Start the worktree’s dev servers' : 'Make the worktree again from its branch on GitHub, set it up and start its dev servers'}
        >
          <Play />
          {d.exists ? 'Start' : 'Set up and start'}
        </Button>
      ) : null}
      {canStop && d.exists && d.running ? (
        <Button
          variant="outline"
          size="xs"
          disabled={offline || working}
          loading={busy === 'stop' || pending === 'stop'}
          loadingText="Stopping…"
          onClick={() => void go('stop')}
          data-hint="Stop the dev servers; the worktree stays"
        >
          <Square />
          Stop
        </Button>
      ) : null}
      {canStop && d.exists ? (
        <Button
          variant="outline"
          size="xs"
          disabled={offline || working || runActive}
          loading={busy === 'tidy' || pending === 'tidy'}
          loadingText="Tidying…"
          onClick={() => void go('tidy')}
          data-hint={runActive ? `R-${d.run.number} is still going in this worktree` : 'Remove the worktree from the machine, with its servers, ports and database branch'}
        >
          <Trash2 />
          Tidy
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The task's dev servers panel: the worktree of its newest run, on which machine, its servers' links, what was
 * last done to them, Start, Stop and Tidy, and how long they may sit idle before they stop themselves.
 * SF-T-305: with no servers and nothing going on (or just failed), it's one line, `heading` first, with no idle
 * time (it only matters once there are servers); otherwise `heading` sits over the box.
 */
export function TaskDevServersPanel({
  devServers: d,
  canStart,
  canStop,
  onChanged,
  project,
  run,
  heading,
}: {
  devServers: TaskDevServers;
  canStart: boolean;
  canStop: boolean;
  onChanged?: () => void;
  project?: string;
  run?: typeof runDevServerAction;
  /** The section's title (TaskDetailView's "Dev servers"): in the line when it's one line, over the box otherwise. */
  heading?: string;
}) {
  const [idle, setIdle] = useState<number | null>(d.task_idle_minutes);
  // Saved elsewhere since (another person, a reload): the field follows.
  const [seen, setSeen] = useState(d.task_idle_minutes);
  if (seen !== d.task_idle_minutes) {
    setSeen(d.task_idle_minutes);
    setIdle(d.task_idle_minutes);
  }
  const [savingIdle, setSavingIdle] = useState(false);
  const idleChanged = idle !== d.task_idle_minutes;
  const saveIdle = async () => {
    setSavingIdle(true);
    const res = await setTaskDevIdle(d.task.id, idle, { project });
    setSavingIdle(false);
    if (!res.data) {
      toast.error(describeApiError(res, "Couldn't save the idle time"));
      return;
    }
    onChanged?.();
  };
  const action = d.action;
  const progress = useDevProgress(d);
  const settling = devProgressLine(d, progress);
  // A Start that finished with nothing listening keeps the whole box (its line and the action's), so it never
  // reads as a worktree stopped on purpose once the stuck warning has gone.
  const oneLine = !d.servers.length && !progress && !settling && action?.action !== 'start' && action?.status !== 'running' && action?.status !== 'failed';
  const box = (
    <div className={cn('rounded-md border', oneLine ? 'px-3 py-2' : 'space-y-2 p-3')}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {heading && oneLine ? <h3 className="mr-1 text-sm font-semibold">{heading}</h3> : null}
        <StatusBadge meta={devServersMeta(d, progress)} size="sm" dot={d.running || action?.status === 'running' || progress?.phase === 'awaiting'} />
        <span className="text-muted-foreground">
          on {d.machine.name}, R-{d.run.number}
          {d.run.branch ? (
            <>
              {' '}
              · <span className="font-mono text-2xs">{d.run.branch}</span>
            </>
          ) : null}
        </span>
        <span className="ml-auto">
          <DevServerActions devServers={d} canStart={canStart} canStop={canStop} onDone={onChanged} project={project} run={run} />
        </span>
      </div>
      {oneLine ? null : (
        <>
      <DevServerLinks servers={d.servers} />
      {d.exists && !d.servers.length && !progress ? <p className="text-xs text-muted-foreground">No dev servers claimed in this worktree yet.</p> : null}
      {progress ? <DevProgressSteps progress={progress} /> : null}
      {settling ? (
        <p className={cn('text-xs', progress?.phase === 'stuck' ? 'text-warning-strong' : 'text-muted-foreground')}>{settling}</p>
      ) : action ? (
        <p className={action.status === 'failed' ? 'text-xs text-danger-strong' : 'flex items-center gap-1.5 text-xs text-muted-foreground'}>
          {action.status === 'running' && !progress ? <Spinner size="xs" /> : null}
          {action.message ?? `${actionRunningLabel(action.action)}…`}
          {action.by ? ` · ${action.by}` : ''} · {formatRelative(action.at)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-muted-foreground">Stop after idle for</span>
        <NumberInput
          aria-label="Minutes idle before the dev servers stop"
          value={idle}
          onValueChange={setIdle}
          min={DEV_IDLE_MINUTES_MIN}
          max={DEV_IDLE_MINUTES_MAX}
          integer
          allowEmpty
          placeholder={String(d.project_idle_minutes)}
          className="h-7 w-20"
          disabled={!canStart}
        />
        <span className="text-muted-foreground">minutes{idle === null ? ` (the project's ${devIdleText(d.project_idle_minutes)})` : ''}</span>
        {idleChanged ? (
          <Button size="xs" variant="outline" loading={savingIdle} onClick={() => void saveIdle()}>
            Save
          </Button>
        ) : null}
      </div>
        </>
      )}
    </div>
  );
  if (!heading) return box;
  return oneLine ? (
    box
  ) : (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{heading}</h3>
      {box}
    </div>
  );
}
